import instantsearch from 'instantsearch.js'
import { productsConfig } from '../../config/algolia.js'
import { connectHits } from 'instantsearch.js/es/connectors'
import { configure } from '../../widgets/algolia/products/'
import { createWidgetInstances } from '../../widgets/algolia/products/createWidgetInstances'
import { historyRouter } from '../../widgets/algolia/products/historyRouter'
import { createPagination } from '../../widgets/algolia/products/pagination'

const algoliaSearch = require('algoliasearch/lite')

export default ($el) =>
  ({
    results: null,
    appId: productsConfig.app_id,
    apiKey: productsConfig.api_key,
    indexName: productsConfig.index,
    isLoaded: false,
    sortedItems: [],
    sortCol: null,
    sortAsc: false,
    selectedAttributes: [],
    availableAttributes: [],
    el: $el,

    // This integer represents the default variable columns (i.e. Part Number)
    get totalColumnsCount() {
      return 1 + this.selectedAttributes.length
    },

    init() {
      this.setupWatchers()
      this.loadAvailableAttributes()
      this.updateColumns()
      this.setupSearch()
    },

    sortBy(event) {
      const { target } = event
      const { col } = event.target.dataset
      let { sortDir } = event.target.dataset

      // Check if sorting is disabled
      if (this.disableSorting) {
        return // Exit the function if sorting is disabled
      }

      if (this.sortCol === col) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortCol = col
        this.sortAsc = true
      }

      sortDir = this.sortAsc ? 'asc' : 'desc'
      target.dataset.sortDir = sortDir

      this.sortResults()
    },

    sortResults() {
      if (!this.results || !this.sortCol) return

      this.results.sort((a, b) => {
        const aValue = a[this.sortCol] || ''
        const bValue = b[this.sortCol] || ''
        return this.sortAsc ?
          aValue.localeCompare(bValue) :
          bValue.localeCompare(aValue)
      })
    },
    setupWatchers() {
      this.$watch('selectedAttributes', () => {
        this.updateColumns()
      })
    },

    loadAvailableAttributes() {
      try {
        this.availableAttributes = JSON.parse(this.el.dataset.availableAttributes)
      } catch (e) {
        console.error('Invalid JSON string in data-available-attributes:', e)
      }
    },

    updateColumns() {
      this.el.style.setProperty('--total-columns', this.totalColumnsCount)
    },

    ensureLeadingSlash(uri) {
      return uri.startsWith('/') ? uri : `/${uri}`
    },

    setupSearch() {
      const algoliaClient = algoliaSearch(this.appId, this.apiKey)
      const search = instantsearch({
        indexName: this.indexName,
        searchClient: algoliaClient,
        future: {
          preserveSharedStateOnUnmount: true,
        },
        routing: {
          router: historyRouter(),
        },
      })

      this.setSelectedAttributesFromData()
      this.updateColumns()

      const widgetContainers = Array.from(document.querySelectorAll('[data-widget-auto]'))
      const widgetInstances = createWidgetInstances(widgetContainers)

      const structuredResults = this.generateStructuredResults()
      search.addWidgets([configure, ...widgetInstances, structuredResults(), createPagination()])
      search.start()

      search.on('render', () => {
        this.onSearchRender(search)
        this.updateSortingState(search)
      })
    },

    setSelectedAttributesFromData() {
      const defaultAttributes = this.el.dataset.tableDefault?.trim() ?? ''
      this.selectedAttributes = defaultAttributes.length > 0 ?
        defaultAttributes.split('|')
          .map((attribute) =>
            attribute.trim()) :
        []
    },

    generateStructuredResults() {
      return connectHits(({ hits }) => {
        this.noResults = hits.length === 0
        this.results = hits.length ? hits : []

        this.sortCol = 'title'
        this.sortAsc = true
        this.sortResults()
      })
    },
    onSearchRender(search) {
      this.isLoaded = true
      const {
        disjunctiveFacetsRefinements,
        numericRefinements,
      } = search.helper.state

      const activeDisjunctiveRefinements = this.getApplicableAttributes(disjunctiveFacetsRefinements)
      const numericActiveRefinements = this.getApplicableAttributes(numericRefinements)
      this.selectedAttributes = [...activeDisjunctiveRefinements, ...numericActiveRefinements]
    },
    getApplicableAttributes(refinements) {
      return Object.keys(refinements)
        .filter((attribute) => {
          const isArray = Array.isArray(refinements[attribute])
          // eslint-disable-next-line max-len
          const hasRefinements = isArray ? refinements[attribute].length > 0 : Object.keys(refinements[attribute]).length > 0
          const isAvailable = this.availableAttributes.includes(attribute)

          return hasRefinements && isAvailable
        })
    },

    updateSortingState(search) {
      const { nbPages } = search.helper.lastResults || {}
      this.disableSorting = nbPages > 1

      // Update UI to reflect disabled state
      const sortHeaders = this.el.querySelectorAll('[data-col]')
      sortHeaders.forEach((header) => {
        if (this.disableSorting) {
          header.classList.add('disabled')
          header.setAttribute('data-sort-disabled', 'true')

          // Replace sort icon with disabled icon
          const sortIcon = header.querySelector('.sort-icon')
          if (sortIcon) {
            sortIcon.classList.add('disabled-sort-icon')
            sortIcon.classList.remove('active-sort-icon')
          }
        } else {
          header.classList.remove('disabled')
          header.removeAttribute('data-sort-disabled')

          // Restore sort icon state
          const sortIcon = header.querySelector('.sort-icon')
          if (sortIcon) {
            sortIcon.classList.remove('disabled-sort-icon')
            sortIcon.classList.add('active-sort-icon')
          }
        }
      })
    },
  })
